import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ModernHeader from '../components/ModernHeader';
import SiteCard from '../components/SiteCard';
import Footer from '../components/Footer';
import CompactBooking from './CompactBooking';
import { API_BASE_URL } from '../config/api';
import { useSettings } from '../context/SettingsContext';

const SitePage = () => {
  const { settings } = useSettings();
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTenantInfo();
    fetchSiteData();
  }, [siteId]);

  const fetchTenantInfo = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user.tenantId;
      
      const response = await axios.get(`${API_BASE_URL}/api/public/tenant-info`, {
        headers: tenantId ? { 'x-tenant-id': tenantId } : {}
      });
      setTenant(response.data.tenant);
    } catch (error) {
      console.error('Error fetching tenant info:', error);
    }
  };

  const fetchSiteData = async () => {
    try {
      setLoading(true);
      
      // Fetch all tickets
      const ticketsResponse = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
      const allTickets = ticketsResponse.data.tickets || [];
      
      // Try to find site across all tenants if no tenant context
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      let tenantId = user.tenantId || tenant?._id;
      
      // If no tenant context, try to find the site by ID across all tenants
      if (!tenantId) {
        try {
          const allSitesResponse = await axios.get(`${API_BASE_URL}/api/public/sites`);
          const allSites = allSitesResponse.data.sites || [];
          const targetSite = allSites.find(s => s._id === siteId);
          if (targetSite) {
            // Found the site, now get its tenant's sites
            tenantId = targetSite.tenantId;
          }
        } catch (error) {
          console.error('Error finding site across tenants:', error);
        }
      }
      
      const sitesResponse = await axios.get(`${API_BASE_URL}/api/public/sites`, {
        headers: tenantId ? { 'x-tenant-id': tenantId } : {}
      });
      const allSites = sitesResponse.data.sites || [];
      const targetSite = allSites.find(s => s._id === siteId);
      
      if (targetSite) {
        const siteTickets = allTickets.filter(ticket => ticket.siteId === siteId);
        setSite(targetSite);
        setTickets(siteTickets);
      } else {
        setSite(null);
        setTickets([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching site data:', error);
      setLoading(false);
    }
  };

  const handleBookNow = (ticket) => {
    setSelectedTicket(ticket);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎫</div>
          <div style={{ fontSize: '1.2rem', color: '#64748b' }}>Loading site...</div>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>❌</div>
          <div style={{ fontSize: '1.2rem', color: '#64748b' }}>Site not found</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)'
    }}>
      <ModernHeader tenant={tenant} />
      
      {!selectedTicket ? (
        <>
          {/* Site Hero Section */}
          <div style={{
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              animation: 'float 6s ease-in-out infinite'
            }} />
            
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', width: '100%', zIndex: 2 }}>
              <div style={{ textAlign: 'center', color: 'white' }}>
                <h1 style={{
                  fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                  fontWeight: '700',
                  marginBottom: '16px',
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}>
                  Welcome to {site.name}
                </h1>
                <p style={{
                  fontSize: '1.2rem',
                  marginBottom: '24px',
                  opacity: 0.9,
                  maxWidth: '600px',
                  margin: '0 auto 24px'
                }}>
                  {site.description}
                </p>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'rgba(255,255,255,0.2)',
                  padding: '12px 24px',
                  borderRadius: '50px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>📍</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>{site.city}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tickets Section */}
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '8px'
              }}>
                Available Tickets
              </h2>
              <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
                Choose from {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} available
              </p>
            </div>

            {tickets.length > 0 ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '24px'
              }}>
                {tickets.map((ticket, index) => (
                  <div 
                    key={ticket._id}
                    style={{ 
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <SiteCard 
                      site={ticket} 
                      onBook={handleBookNow}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎫</div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                  No Tickets Available
                </h3>
                <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
                  Tickets for {site.name} are currently not available. Please check back later!
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
          <CompactBooking 
            selectedTicket={selectedTicket}
            onBack={() => setSelectedTicket(null)}
          />
        </div>
      )}
      
      <Footer />
      
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SitePage;